import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import api from "../../api/axiosClient";
import { useAuth } from "../../context/AuthContext";

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Raleway:wght@300;400;500;600;700;800;900&display=swap');
@import url('https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css');

*, *::before, *::after {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

.cl-root {
  min-height: 100vh;
  display: grid;
  grid-template-columns: 1fr 1fr;
  font-family: 'Raleway', sans-serif;
  background: #f8fafc;
}

/* ───────────────── LEFT PANEL ───────────────── */

.cl-left {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 56px 60px;
  overflow: hidden;

  background:
    linear-gradient(
      rgba(0,0,0,0.70),
      rgba(0,0,0,0.70)
    ),
    url('https://images.unsplash.com/photo-1504307651254-35680f356dfd?q=80&w=1600&auto=format&fit=crop')
    center center/cover no-repeat;
}

.cl-headline {
  position: relative;
  z-index: 1;

  font-size: clamp(2rem, 3vw, 2.8rem);
  font-weight: 900;
  color: #fff;

  line-height: 1.1;
  margin-bottom: 18px;
  letter-spacing: -.03em;
}

.cl-headline em {
  font-style: italic;
  color: #fff000;
  font-weight: 800;
}

.cl-desc {
  position: relative;
  z-index: 1;

  font-size: .9rem;
  color: rgba(255,255,255,.78);

  line-height: 1.85;
  max-width: 380px;
  margin-bottom: 48px;
  font-weight: 400;
}

.cl-perks {
  position: relative;
  z-index: 1;

  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 13px;
}

.cl-perk {
  display: flex;
  align-items: center;
  gap: 11px;

  font-size: .82rem;
  color: rgba(255,255,255,.88);
  font-weight: 500;
}

.cl-tick {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  flex-shrink: 0;

  background: rgba(255,215,0,.14);
  border: 1.5px solid rgba(255,215,0,.35);

  display: flex;
  align-items: center;
  justify-content: center;

  font-size: .5rem;
  color: #fff000;
}

/* ───────────────── RIGHT PANEL ───────────────── */

.cl-right {
  background: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 64px 68px;
}

.cl-box {
  width: 100%;
  max-width: 420px;
}

.cl-brand {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 44px;
}

.cl-title {
  font-size: 2rem;
  font-weight: 900;
  color: #0d0d0d;

  margin-bottom: 5px;
  letter-spacing: -.03em;
}

.cl-sub {
  font-size: .84rem;
  color: #64748b;

  margin-bottom: 32px;
  font-weight: 500;
}

.cl-err {
  background: #fef2f2;
  border: 1.5px solid #fecaca;
  border-radius: 10px;

  padding: 11px 14px;
  margin-bottom: 20px;

  font-size: .8rem;
  color: #b91c1c;

  display: flex;
  align-items: center;
  gap: 8px;

  font-weight: 600;
}

.cl-field {
  margin-bottom: 18px;
}

.cl-lbl {
  display: block;

  font-size: .68rem;
  font-weight: 700;

  text-transform: uppercase;
  letter-spacing: .08em;

  color: #475569;
  margin-bottom: 7px;
}

.cl-inp {
  width: 100%;
  padding: 13px 15px;

  font-size: .9rem;
  font-weight: 500;

  border: 2px solid #e2e8f0;
  border-radius: 12px;

  background: #fff;
  color: #1e293b;
  font-family: 'Raleway', sans-serif;
  outline: none;

  transition: border-color .2s, box-shadow .2s;
}

.cl-inp:focus {
  border-color: #fff000;
  box-shadow: 0 0 0 4px rgba(255,215,0,.12);
}

.cl-inp:disabled {
  background: #f8fafc;
  cursor: not-allowed;
}

.cl-pw-wrap {
  position: relative;
}

.cl-eye {
  position: absolute;
  right: 13px;
  top: 50%;
  transform: translateY(-50%);

  background: none;
  border: none;

  color: #94a3b8;
  cursor: pointer;

  font-size: .84rem;
  padding: 4px;

  transition: color .15s;
  line-height: 1;
}

.cl-eye:hover {
  color: #0d0d0d;
}

.cl-row {
  display: flex;
  justify-content: flex-end;
  align-items: center;

  margin-bottom: 24px;
}

.cl-forgot {
  font-size: .8rem;
  font-weight: 700;

  color: #0d0d0d;
  text-decoration: none;

  transition: color .15s;
}

.cl-forgot:hover {
  color: #e6c200;
}

.cl-btn {
  width: 100%;
  padding: 14px;

  font-size: .92rem;
  font-weight: 800;

  background: linear-gradient(135deg, #fff000, #e6c200);
  color: #000000;

  border: none;
  border-radius: 12px;

  cursor: pointer;
  font-family: 'Raleway', sans-serif;
  transition: all .2s;

  box-shadow: 0 6px 22px rgba(255,215,0,.35);

  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  letter-spacing: -.01em;
}

.cl-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 12px 36px rgba(255,215,0,.45);
  filter: brightness(1.05);
}

.cl-btn:disabled {
  opacity: .5;
  cursor: not-allowed;
  transform: none;
}

@keyframes cl-spin {
  to {
    transform: rotate(360deg);
  }
}

.cl-spin {
  display: inline-block;
  width: 14px;
  height: 14px;

  border: 2.5px solid rgba(0,0,0,.2);
  border-top-color: #0d0d0d;
  border-radius: 50%;

  animation: cl-spin .7s linear infinite;
}

.cl-div {
  display: flex;
  align-items: center;
  gap: 12px;

  margin: 26px 0;
}

.cl-div-line {
  flex: 1;
  height: 1px;
  background: #e2e8f0;
}

.cl-div-txt {
  font-size: .68rem;
  font-weight: 700;

  color: #94a3b8;

  text-transform: uppercase;
  letter-spacing: .08em;
}

.cl-google-wrap {
  margin-bottom: 1.5rem;
}

.cl-signup {
  text-align: center;
  font-size: .83rem;
  color: #64748b;
}

.cl-signup a {
  color: #0d0d0d;
  font-weight: 800;
  text-decoration: none;

  border-bottom: 2px solid #fff000;
  padding-bottom: 1px;

  transition: color .15s;
}

.cl-signup a:hover {
  color: #e6c200;
}

/* ───────────────── MOBILE ───────────────── */

@media (max-width: 780px) {
  .cl-root {
    grid-template-columns: 1fr;
  }

  .cl-left {
    display: none;
  }

  .cl-right {
    padding: 44px 24px;
    background: #fff;
  }
}
`;

const GOOGLE_CLIENT_ID =
  "551247510793-ria1stm1obcn36nkkl2is4tknoqaj2sv.apps.googleusercontent.com";

// Where to go when there is no ?next= in the URL.
// MUST be a real route in your router. The backend's own default for
// craftsmen is "/craftsman/dashboard" - change this one line if that is
// the route your app actually uses.
const DEFAULT_AFTER_LOGIN = "/dashboard";

// Only allow redirects to paths inside this app ("/jobs/18"), never to
// another website ("https://evil.com" or "//evil.com").
const safeNextPath = (value) =>
  typeof value === "string" && value.startsWith("/") && !value.startsWith("//")
    ? value
    : DEFAULT_AFTER_LOGIN;

function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const googleButtonRef = useRef(null);

  // Populated when the person was bounced here from a protected action
  // (e.g. clicking "Apply Now" on a job while logged out - the job page
  // links here as `/login?next=/jobs/18`).
  const nextPath = safeNextPath(searchParams.get('next'));

  const handleLogin = async (e) => {
    e.preventDefault();

    setError('');

    if (!email || !password) {
      return setError("Email and password are required.");
    }

    setLoading(true);

    try {
      const res = await api.post("/login/", { email, password });

      // Password was correct but the account has 2FA enabled - no auth
      // cookies have been set yet, so don't try to load the user.
      if (res.data?.two_factor_required) {
        navigate('/verify-2fa', {
          state: {
            pendingToken: res.data.pending_token,
            method: res.data.method,
            // Carry the redirect through 2FA too, so completing it also
            // lands back on the job instead of the dashboard.
            next: nextPath,
          },
        });
        return;
      }

      // Reloads /me/ into AuthContext, so protected pages see the session.
      await login();

      navigate(nextPath, { replace: true });

    } catch (err) {
      console.error("Login failed:", err.response?.status, err.response?.data || err.message);

      setError(
        err.response?.data?.detail || "Login failed. Please check your details and try again."
      );

    } finally {
      setLoading(false);
    }
  };

  const handleCredentialResponse = async (response) => {
    const token = response?.credential;

    if (!token) {
      setError("Invalid Google credential. Please try again.");
      return;
    }

    setError('');
    setLoading(true);

    try {
      const res = await api.post("/google-login/", {
        token,
        role: "craftsman"
      });

      if (res.data?.two_factor_required) {
        navigate('/verify-2fa', {
          state: {
            pendingToken: res.data.pending_token,
            method: res.data.method,
            next: nextPath,
          },
        });
        return;
      }

      await login();

      const userData = res.data?.user || {};

      if (!userData.phone_number) {
        document.cookie = `profileIncomplete=true; max-age=${30 * 24 * 3600}; path=/`;
      }

      navigate(nextPath, { replace: true });

    } catch (err) {
      console.error("Google login failed:", err.response?.status, err.response?.data || err.message);

      setError(
        err.response?.data?.detail ||
        "Google login failed. Please try again."
      );

    } finally {
      setLoading(false);
    }
  };

  // Keep the latest handler available to Google's callback (it is only
  // registered once, so without this it would use stale values).
  const googleHandlerRef = useRef(handleCredentialResponse);
  googleHandlerRef.current = handleCredentialResponse;

  useEffect(() => {
    let cancelled = false;
    let tries = 0;

    
    const setup = () => {
      if (cancelled) return;

      if (window.google && googleButtonRef.current) {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: (response) => googleHandlerRef.current(response),
        });

        window.google.accounts.id.renderButton(googleButtonRef.current, {
          theme: "outline",
          size: "large",
          type: "standard",
          text: "signin_with",
        });
        return;
      }

      tries += 1;
      if (tries < 40) setTimeout(setup, 250); // retry for about 10 seconds
    };

    setup();
    return () => { cancelled = true; };
  }, []);

  return (
    <>
      <style>{CSS}</style>

      <div className="cl-root">

        {/* LEFT SIDE */}
        <div className="cl-left">

          <h1 className="cl-headline">
            Build your craft.
            <br />
            Grow your
            <br />
            <em>business.</em>
          </h1>

          <p className="cl-desc">
            Kenya's verified marketplace connecting skilled craftsmen
            with clients who need quality work done right.
          </p>

          <ul className="cl-perks">
            {[
              'Get assigned jobs directly to your dashboard',
              'Send professional quotes to clients',
              'Track job progress from start to payment',
              'Build your reputation with verified reviews',
            ].map((text, i) => (
              <li className="cl-perk" key={i}>
                <span className="cl-tick">
                  <i className="fas fa-check" />
                </span>

                {text}
              </li>
            ))}
          </ul>
        </div>

        {/* RIGHT SIDE */}
        <div className="cl-right">

          <div className="cl-box">

            <div className="cl-brand"></div>

            <h2 className="cl-title">
              Welcome back
            </h2>

            <p className="cl-sub">
              Log in to your craftsman dashboard
            </p>

            {error && (
              <div className="cl-err">
                <i className="fas fa-exclamation-circle" />
                {error}
              </div>
            )}

            <form onSubmit={handleLogin}>

              <div className="cl-field">
                <label
                  className="cl-lbl"
                  htmlFor="email"
                >
                  Email address
                </label>

                <input
                  id="email"
                  type="email"
                  className="cl-inp"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) =>
                    setEmail(e.target.value)
                  }
                  required
                  disabled={loading}
                  autoComplete="email"
                />
              </div>

              <div className="cl-field">

                <label
                  className="cl-lbl"
                  htmlFor="password"
                >
                  Password
                </label>

                <div className="cl-pw-wrap">

                  <input
                    id="password"
                    type={
                      showPassword
                        ? 'text'
                        : 'password'
                    }
                    className="cl-inp"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) =>
                      setPassword(e.target.value)
                    }
                    required
                    disabled={loading}
                    style={{ paddingRight: 44 }}
                    autoComplete="current-password"
                  />

                  <button
                    type="button"
                    className="cl-eye"
                    onClick={() =>
                      setShowPassword(!showPassword)
                    }
                    tabIndex="-1"
                  >
                    <i
                      className={`fas ${
                        showPassword
                          ? 'fa-eye-slash'
                          : 'fa-eye'
                      }`}
                    />
                  </button>

                </div>
              </div>

              <div className="cl-row">
                <Link
                  to="/forgot-password"
                  className="cl-forgot"
                >
                  Forgot password?
                </Link>
              </div>

              <button
                type="submit"
                className="cl-btn"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="cl-spin" />
                    Logging in…
                  </>
                ) : (
                  <>
                    <i className="fas fa-arrow-right-to-bracket" />
                    Log in
                  </>
                )}
              </button>

            </form>

            <div className="cl-div">

              <div className="cl-div-line" />

              <span className="cl-div-txt">
                Or continue with
              </span>

              <div className="cl-div-line" />

            </div>

            <div
              className="cl-google-wrap"
              ref={googleButtonRef}
            />

            <p className="cl-signup">
              Don't have an account?{" "}

              <Link to="/signup">
                Sign up free
              </Link>
            </p>

          </div>
        </div>
      </div>
    </>
  );
}

export default LoginPage;